<template>
  <HighlightsWidget
    :disableZeroState="true"
    :flexLayout="true"
    fullLinkTitle="activity log"
    :isLoading="isLoading"
    :showLinkToFullList="true"
    title="Device Activity"
    v-on="$listeners"
  >
    <template v-slot:mainContent>
      <table
        :class="$style.table"
      >
        <TableHeader
          :columns="tableColumns"
        />
        <TableBody
          :columns="tableColumns"
          :inContainer="true"
          :isRefreshing="isLoading"
          :tableData="tableData"
        />
      </table>
    </template>
  </HighlightsWidget>
</template>

<script>

import { ItemTableConsts } from 'client/util/Constants';
import { formatMetrics } from 'client/components/Highlights/highlightsCommonUtils';
import EventMetadata, { eventFields } from 'client/Insights/ActivityLog/constants/EventMetadata';
import EventTypes from 'client/Insights/ActivityLog/constants/EventTypes';
import HIGHLIGHTS_CONSTS from 'client/components/Highlights/constants/highlightsConsts';
import HighlightsWidget from 'client/components/Highlights/HighlightsWidget.vue';
import InsightsAccessMixin from 'client/Insights/InsightsAccessMixin';
import TableBody from 'components/Tables/TableBody.vue';
import TableHeader from 'components/Tables/TableHeader.vue';
import groupsIcon from 'public/img/icons/usersLink.svg?inline';

const { parentOSType } = ItemTableConsts;

const {
  EVENT_FIELD,
  STATUS_FIELD,
  TIMESTAMP_FIELD,
  ID_FIELD,
} = HIGHLIGHTS_CONSTS;

const WINDOWS_MACHINE_ONLY_DEFAULT_FIELDS = ['authenticationWithElevatedPrivileges'];
const DEFAULT_ACTIVITY_FIELDS = [
  'lastNewUserAssigned',
  'lastUserLogin',
  'deviceGroupChange',
  'userAdminGranted',
  'userAdminRevoked',
  'lastAgentReportTime',
];

export default {
  name: 'DeviceEventSummaryWidget',

  components: {
    HighlightsWidget,
    TableBody,
    TableHeader,
  },

  mixins: [InsightsAccessMixin],

  props: {
    itemId: {
      type: String,
      default: '',
    },
    systemDetails: {
      type: Object,
      default: () => {},
    },
  },

  data: () => ({
    icon: groupsIcon,
    isLoading: true,
    metrics: {
      lastNewUserAssigned: {
        [ID_FIELD]: 'LastNewUserAssigned',
        [EVENT_FIELD]: 'Last New User Assigned',
        [STATUS_FIELD]: '',
        [TIMESTAMP_FIELD]: '',
      },
      lastUserLogin: {
        [ID_FIELD]: 'LastUserLogin',
        [EVENT_FIELD]: 'Last User Login',
        [STATUS_FIELD]: '',
        [TIMESTAMP_FIELD]: '',
      },
      deviceGroupChange: {
        [ID_FIELD]: 'DeviceGroupChange',
        [EVENT_FIELD]: 'Device Group Change',
        [STATUS_FIELD]: '',
        [TIMESTAMP_FIELD]: '',
      },
      userAdminGranted: {
        [ID_FIELD]: 'UserAdminGranted',
        [EVENT_FIELD]: 'User Admin Granted',
        [STATUS_FIELD]: '',
        [TIMESTAMP_FIELD]: '',
      },
      userAdminRevoked: {
        [ID_FIELD]: 'UserAdminRevoked',
        [EVENT_FIELD]: 'User Admin Revoked',
        [STATUS_FIELD]: '',
        [TIMESTAMP_FIELD]: '',
      },
      authenticationWithElevatedPrivileges: {
        [ID_FIELD]: 'AuthenticationWithElevatedPrivileges',
        [EVENT_FIELD]: 'Authentication with Elevated Privileges',
        [STATUS_FIELD]: '',
        [TIMESTAMP_FIELD]: '',
      },
      lastAgentReportTime: {
        [ID_FIELD]: 'LastAgentReportTime',
        [EVENT_FIELD]: 'Last Agent Report Time',
        [STATUS_FIELD]: '',
        [TIMESTAMP_FIELD]: '',
      },
    },
    tableColumns: [
      { name: 'Event', dataField: EVENT_FIELD },
      { name: 'Resource/Status', dataField: STATUS_FIELD },
      { name: 'Timestamp', dataField: TIMESTAMP_FIELD },
    ],
  }),

  computed: {
    osIsWindows() {
      return this.systemDetails.osFamily === parentOSType.windows;
    },
    tableData() {
      const allDefaultFields = [
        ...DEFAULT_ACTIVITY_FIELDS,
        ...(this.osIsWindows ? WINDOWS_MACHINE_ONLY_DEFAULT_FIELDS : []),
      ];

      return formatMetrics(this.metrics, allDefaultFields);
    },
  },

  created() {
    Promise.allSettled([
      this.fetchLastNewUserAssigned(),
      this.fetchLastUserLogin(),
      this.fetchLastDeviceGroupChange(),
      this.fetchLastUserAdminGranted(),
      this.fetchLastUserAdminRevoked(),
      ...(this.osIsWindows ? [this.fetchLastAuthenticationWithElevatedPrivileges()] : []),
    ])
      .finally(() => {
        this.setLastAgentReportTimesync();
        this.isLoading = false;
      });
  },

  methods: {
    async fetchLastAuthenticationWithElevatedPrivileges() {
      const { authenticationWithElevatedPrivileges } = this.metrics;
      const event = await this.getLastEvent(
        EventTypes.loginAttempt,
        [
          { [EventMetadata[[eventFields.windowsElevated]].path]: 'true' },
          { [EventMetadata[[eventFields.systemId]].path]: this.itemId },
        ],
      );

      if (event) {
        authenticationWithElevatedPrivileges[STATUS_FIELD] = event.initiated_by.username
        || event.initiated_by.email;
        authenticationWithElevatedPrivileges[TIMESTAMP_FIELD] = this.getTimestampValue(event);
      }
    },

    async fetchLastDeviceGroupChange() {
      const { deviceGroupChange } = this.metrics;
      const event = await this.getLastEvent(
        EventTypes.associationChange,
        [
          { [EventMetadata[[eventFields.associationFromType]].path]: 'SYSTEM_GROUP' },
          { [EventMetadata[[eventFields.associationToId]].path]: this.itemId },
        ],
      );

      if (event) {
        const verbage = event.association.op === 'add' ? 'Added to' : 'Removed from';
        const uiValue = `${verbage} ${event.association.connection.from.name}`;
        deviceGroupChange[STATUS_FIELD] = uiValue;
        deviceGroupChange[TIMESTAMP_FIELD] = this.getTimestampValue(event);
      }
    },

    async fetchLastNewUserAssigned() {
      const { lastNewUserAssigned } = this.metrics;

      // Grab both events and filter null responses
      // It's necessary to grab both
      // since event order / presence isn't guaranteed
      const events = await Promise.all([
        // User added to device from device aside
        this.getLastEvent(
          EventTypes.associationChange,
          [
            { [EventMetadata[eventFields.associationFromId].path]: this.itemId },
            { [EventMetadata[eventFields.associationChangeTo].path]: 'USER' },
          ],
        ),
        // Device added to user from user aside
        this.getLastEvent(
          EventTypes.associationChange,
          [
            { [EventMetadata[eventFields.associationToId].path]: this.itemId },
            { [EventMetadata[eventFields.associationFromType].path]: 'USER' },
          ],
        ),
      ]).then(_events => _events.filter(Boolean));

      if (events.length) {
        // sort events by timestamp (descending)
        // and grab the most recent
        events.sort((eventA, eventB) => new Date(eventB.timestamp) - new Date(eventA.timestamp));

        const [event] = events;

        lastNewUserAssigned[STATUS_FIELD] = event.association.connection.to.type === 'USER'
          ? event.association.connection.to.name
          : event.association.connection.from.name;

        lastNewUserAssigned[TIMESTAMP_FIELD] = this.getTimestampValue(event);
      }
    },

    async fetchLastUserAdminGranted() {
      const { userAdminGranted } = this.metrics;
      const event = await this.getLastEvent(
        EventTypes.userAdminGranted,
        [{ [EventMetadata[[eventFields.systemId]].path]: this.itemId }],
      );

      if (event) {
        userAdminGranted[STATUS_FIELD] = event.resource.username;
        userAdminGranted[TIMESTAMP_FIELD] = this.getTimestampValue(event);
      }
    },

    async fetchLastUserAdminRevoked() {
      const { userAdminRevoked } = this.metrics;
      const event = await this.getLastEvent(
        EventTypes.userAdminRevoked,
        [{ [EventMetadata[[eventFields.systemId]].path]: this.itemId }],
      );

      if (event) {
        userAdminRevoked[STATUS_FIELD] = event.resource.username;
        userAdminRevoked[TIMESTAMP_FIELD] = this.getTimestampValue(event);
      }
    },

    async fetchLastUserLogin() {
      const { lastUserLogin } = this.metrics;
      const event = await this.getLastEvent(
        EventTypes.loginAttempt,
        [
          { [EventMetadata[[eventFields.systemId]].path]: this.itemId },
        ],
      );

      if (event) {
        lastUserLogin[STATUS_FIELD] = event.username;
        lastUserLogin[TIMESTAMP_FIELD] = this.getTimestampValue(event);
      }
    },

    getTimestampValue(event) {
      if (!event) {
        return event;
      }

      const correctEventFormat = event.timestamp ? event : { timestamp: event };

      return EventMetadata[eventFields.timestamp].getDataValue(correctEventFormat);
    },

    setLastAgentReportTimesync() {
      const { lastAgentReportTime } = this.metrics;
      if (this.systemDetails.lastContact) {
        // eslint-disable-next-line max-len
        lastAgentReportTime[TIMESTAMP_FIELD] = this.getTimestampValue(this.systemDetails.lastContact);
        lastAgentReportTime[STATUS_FIELD] = this.systemDetails.agentVersion ? `Agent v${this.systemDetails.agentVersion}` : 'Agent version not available';
      }
    },
  },
};
</script>

<style module>
  .table {
    text-align: left;
    width: 100%;
  }
</style>
